/**
 * MaiaMilk hero banner.
 *
 * Self-contained custom element. Does not touch the theme's carousel.
 *
 * Behaviour notes:
 * - Autoplay is disabled entirely for visitors who prefer reduced motion.
 *   Auto-advancing content with no pause control fails WCAG 2.2.2, and arrows
 *   are navigation, not a pause mechanism.
 * - Playback also stops when the tab is hidden or the banner scrolls out of
 *   view, so a phone is not animating a banner nobody is looking at.
 */
class MaiaHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-maia-slide]'));
    if (this.slides.length === 0) return;

    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    this.progress = this.querySelector('[data-maia-progress]');
    this.counter = this.querySelector('[data-maia-count]');

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interval = this.reduceMotion ? 0 : (parseFloat(this.dataset.interval) || 0) * 1000;

    this.timer = null;
    this.paused = false;
    this.visible = true;

    this.indexRevealTargets();
    this.bindControls();
    this.bindSwipe();
    this.bindVisibility();
    this.bindThemeEditor();

    this.setAttribute('data-ready', '');
    this.goTo(this.index, { animate: false });
    this.play();
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /**
   * Number the visible content blocks so the reveal staggers in document order.
   *
   * Each theme block renders a <style> element as a sibling inside .prose, so
   * :nth-child() in CSS would spend delays on invisible nodes and the copy would
   * arrive out of order. Counting here skips them.
   */
  indexRevealTargets() {
    this.slides.forEach((slide) => {
      // A slide can hold several content groups, each with its own .prose.
      // Numbering runs across all of them so the reveal reads as one sequence
      // rather than every group starting over at zero.
      let i = 0;
      slide.querySelectorAll('.prose').forEach((prose) => {
        Array.from(prose.children).forEach((child) => {
          if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') return;
          child.style.setProperty('--maia-i', i);
          i += 1;
        });
      });
    });
  }

  /* ---------------------------------------------------------------- controls */

  bindControls() {
    const prev = this.querySelector('[data-maia-prev]');
    const next = this.querySelector('[data-maia-next]');

    if (prev) prev.addEventListener('click', () => this.step(-1, true));
    if (next) next.addEventListener('click', () => this.step(1, true));

    if (this.hasAttribute('data-pause-on-hover')) {
      this.addEventListener('mouseenter', () => { this.paused = true; this.stop(); });
      this.addEventListener('mouseleave', () => { this.paused = false; this.play(); });
    }

    // Keyboard support when the banner itself has focus.
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1, true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1, true); }
    });

    // A focused link inside a slide should never be scrolled past by autoplay.
    this.addEventListener('focusin', () => { this.paused = true; this.stop(); });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) { this.paused = false; this.play(); }
    });
  }

  bindSwipe() {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.addEventListener('touchstart', (e) => {
      const t = e.changedTouches[0];
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Horizontal intent only — do not hijack vertical scrolling.
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.step(dx < 0 ? 1 : -1, true);
      }
    }, { passive: true });
  }

  bindVisibility() {
    this.onVisibility = () => {
      if (document.hidden) this.stop();
      else this.play();
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.play();
          else this.stop();
        });
      }, { threshold: 0.15 });
      this.observer.observe(this);
    }
  }

  bindThemeEditor() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    // Selecting a slide in the customiser should show that slide and hold it.
    document.addEventListener('shopify:block:select', (e) => {
      const slide = e.target.closest('[data-maia-slide]');
      if (!slide || !this.contains(slide)) return;
      const i = this.slides.indexOf(slide);
      if (i > -1) {
        this.paused = true;
        this.stop();
        this.goTo(i);
      }
    });

    document.addEventListener('shopify:block:deselect', () => {
      this.paused = false;
      this.play();
    });
  }

  /* -------------------------------------------------------------- navigation */

  step(direction, fromUser) {
    const next = (this.index + direction + this.slides.length) % this.slides.length;
    this.goTo(next);
    if (fromUser) this.restart();
  }

  goTo(index, { animate = true } = {}) {
    this.index = index;

    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);

      // Only the active slide's links should be reachable by keyboard.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });

      const video = slide.querySelector('video');
      if (video) {
        if (active && !this.reduceMotion) {
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          video.pause();
        }
      }
    });

    if (this.counter) {
      this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    }

    if (animate) this.resetProgress();
  }

  /* ---------------------------------------------------------------- autoplay */

  play() {
    if (this.interval <= 0 || this.slides.length < 2) return;
    if (this.paused || document.hidden || !this.visible) return;
    this.stop();
    this.setAttribute('data-playing', '');
    this.resetProgress();
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.removeAttribute('data-playing');
  }

  restart() {
    this.stop();
    this.play();
  }

  resetProgress() {
    if (!this.progress || !this.hasAttribute('data-playing')) return;
    // Force the fill animation to start over on every slide change.
    this.progress.style.animation = 'none';
    void this.progress.offsetWidth;
    this.progress.style.animation = '';
  }
}

if (!customElements.get('maia-hero')) {
  customElements.define('maia-hero', MaiaHero);
}
